import type { FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { ProjectSettingsModal } from '@frameio/workspaces/settings/components';

type ProjectSettingsContextValue = {
  selectedProjectId: string | null;
  workspaceId: string | null;
  isSettingsOpen: boolean;
  clickProjectSettings: (projectId?: string, workspaceId?: string) => void;
};

const ProjectSettingsContext = createContext<ProjectSettingsContextValue>({
  selectedProjectId: null,
  workspaceId: null,
  isSettingsOpen: false,
  clickProjectSettings: () => undefined,
});

export const useProjectSettings = () => useContext(ProjectSettingsContext);

type ProjectSwitcherProjectSettingsProps = {
  children: ReactNode;
};

export const ProjectSwitcherProjectSettings: FC<
  ProjectSwitcherProjectSettingsProps
> = ({ children }) => {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null
  );
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const accountId = useActiveAccountId();

  const clickProjectSettings = useCallback(
    (projectId?: string, wsId?: string) => {
      if (!isSettingsOpen && projectId && wsId) {
        setSelectedProjectId(projectId);
        setWorkspaceId(wsId);
        setIsSettingsOpen(true);
      } else {
        setIsSettingsOpen(false);
        setSelectedProjectId(null);
        setWorkspaceId(null);
      }
    },
    [isSettingsOpen]
  );

  const contextValue = useMemo(
    () => ({
      selectedProjectId,
      workspaceId,
      isSettingsOpen,
      clickProjectSettings,
    }),
    [selectedProjectId, workspaceId, isSettingsOpen, clickProjectSettings]
  );

  return (
    <ProjectSettingsContext.Provider value={contextValue}>
      {children}

      {selectedProjectId && workspaceId && (
        <ModalContainer>
          <ProjectSettingsModal
            accountId={accountId}
            isShowing={isSettingsOpen}
            onRequestClose={clickProjectSettings}
            projectId={selectedProjectId}
            trackingSource="ProjectSettingsFromProject"
            workspaceId={workspaceId}
          />
        </ModalContainer>
      )}
    </ProjectSettingsContext.Provider>
  );
};

const ModalContainer = styled.div`
  position: relative;
  z-index: 1;
`;
